import { Deferred } from './Deferred';

/**
 * Represents an awaitable event which can be triggered programmatically.
 */
export default class AutoResetEvent {
    private _awaiter?: Deferred<void>;
    private _awaiterCount = 0;

    /**
     * sets the event state to triggered. This will resolve all awaiters with
     * the value true. The event is reset to un-set automatically.
     */
    public set(): void {
        const awaiter = this._awaiter;
        this._awaiter = undefined;

        awaiter?.resolve();
    }

    public dispose(): void {
        this.set();
    }

    /**
     * Returns a promise which resolves with true when the event is signalled.
     */
    public async waitOne(): Promise<boolean>;
    /**
     * Returns a promise which resolves with true when the event is signalled
     * or returns false when timeout milliseconds elapses without being
     * signalled.
     * @param timeout
     */
    public async waitOne(timeout: number): Promise<boolean>;

    /**
     * Returns a promise which resolves with true when the AutoResetEvent is set to the
     * triggered state or false when the timeout elapses. If no timeout is provided,
     * waitOne will resolve only when the AutoResetEvent is triggered.
     */
    public async waitOne(timeout?: number): Promise<boolean> {
        timeout ??= 0;

        if (!this._awaiter) {
            this._awaiterCount = 1;
            this._awaiter = new Deferred<void>();
        } else {
            this._awaiterCount++;
        }

        const promises = [
            this._awaiter.then(
                () => true,
                () => false
            ),
        ];

        if (timeout > 0) {
            promises.push(
                new Promise((resolve) => {
                    setTimeout(() => resolve(false), timeout);
                })
            );
        }

        const result = await Promise.race(promises);

        this._awaiterCount--;

        if (this._awaiterCount < 1 && this._awaiter) {
            this._awaiter = undefined;
        }

        return result;
    }
}
